﻿import { ApplicationContextService } from "../../ServiceDesk/sdServices/context/application-context-service";
import { AwaEventTracker, CustomEventWithSDUniqueId } from "./awa-event-tracker";
import {
  ContentPageAction,
  ContentLoadEvent,
  CustomKeyValues,
  PageActionEvent,
  ContentUpdateEvent
} from "./telemetry-service";
import { GlobalTelemetryConstants } from "./telemetry-constants";
import * as Jsll from "./jsll-types";

declare var awa: any;

export const telemetryFlushInterval = 10000;

export class TelemetryLoggerService {
  private $state: ng.ui.IStateService; // This is filled lazily.

  // @ngInject
  constructor(
    private $injector: ng.auto.IInjectorService,
    private $interval: ng.IIntervalService,
    private applicationContextService: ApplicationContextService,
    private awaEventTracker: AwaEventTracker,
    private $window: ng.IWindowService,
    private telemetryConstants: GlobalTelemetryConstants
  ) {
    let asimovValidateEvent = awa._validateAndTranslateEvent;
    // Override validateAndTranslate to make sure we always have a time property.
    awa._validateAndTranslateEvent = (event: { time: Date }) => {
      let result: any;

      result = asimovValidateEvent.bind(awa)(event);
      result.event.time = event.time || new Date();
      return result;
    };
    awaEventTracker.initialize(this);

    $interval(() => {
      awa.vortexEvents.batchQueuedEvents();
    }, telemetryFlushInterval);
  }

  public getCorrelationVectorHeader(): string {
    return "MS-CV";
  }

  public getCorrelationVector(): string {
    return this.applicationContextService.appContext.windowCV;
  }

  // This should be incremented right before every service call.
  public incrementCorrelationVector(): string {
    return this.applicationContextService.incrementCV();
  }

  public capturePageView(pageName?: string, referrerUri?: string): void {
    let overrides: Jsll.IOverrideValues = {
      pageName: pageName ? pageName : this.getPageName()
    };
    if (referrerUri) {
      overrides.referrerUri = referrerUri;
      overrides.uri = referrerUri;
    }

    awa.ct.capturePageView(overrides);
  }

  public pushEvent(event: Jsll.ICustomEvent, logImmediately?: boolean) {
    this.awaEventTracker.trackEvent(<CustomEventWithSDUniqueId>event);
    awa.vortexEvents.SendOrScheduleEvent(event, logImmediately);
  }

  public capturePageAction(
    element: ng.IRootElementService,
    behavior: any,
    content: ContentPageAction,
    actionType?: string,
    pageTags?: CustomKeyValues,
    serviceName?: string
  ): void {
    awa.ct.capturePageAction(<any>element, {
      behavior: behavior,
      contentTags: content,
      serviceName: serviceName,
      pageTags: pageTags,
      actionType: actionType
    });
  }

  public captureContentPageAction(event: PageActionEvent): void {
    awa.ct.captureContentPageAction({
      behavior: event.behavior || this.telemetryConstants.pageActionBehavior.ProcessCheckpoint,
      content: event.content,
      pageTags: event.pageTags,
      actionType: event.actionType || this.telemetryConstants.pageActionType.auto,
      isAuto: false,
      serviceName: event.serviceName
    });
  }

  public captureContentUpdate(event: ContentUpdateEvent): void {
    awa.ct.captureContentUpdate({
      behavior: event.behavior || this.telemetryConstants.pageActionBehavior.PageScroll,
      content: event.content,
      pageTags: event.pageTags,
      actionType: event.actionType || this.telemetryConstants.pageActionType.auto,
      isAuto: false,
      serviceName: event.serviceName
    });
  }

  public captureContentLoadEvent(content: ContentLoadEvent): void {
    awa.ct.captureContentUpdate({
      pageLoadTime: content.durationInMs,
      pageName: content.contentName,
      content: JSON.stringify(content),
      serviceName: content.serviceName
    });
  }

  public extendTelemetryContext(data: Jsll.ICoreData): void {
    awa.extendCoreData(data);
  }

  public getTelemetryContext(): Jsll.ICoreData {
    if (awa) {
      return awa.getConfig().coreData;
    }
  }

  public setAuthUserId(userId: string): void {
    if (userId) {
      awa.ids.setAppUserId(`i:${userId}`);
    }
  }

  public getPageName(): string {
    if (!this.$state) {
      this.$state = this.$injector.get<ng.ui.IStateService>("$state");
    }
    if (this.$state) {
      return this.$state.current.name;
    }
    let hash = this.$window.location.hash;
    if (hash) {
      let parts = hash.split("?")[0].split("/");
      return parts[1] || parts[0];
    }
    return undefined;
  }
}
